import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Link } from "react-router-dom";
import { Search, Plus, Baby, Upload, Download } from "lucide-react";
import { toast } from "sonner";

const CLASSES = ["Playgroup", "Nursery", "LKG", "UKG"];
const PASTELS = ["chip-pink", "chip-green", "chip-teal", "chip-amber", "chip-lilac"];

const emptyChild = {
  name: "", date_of_birth: "", parent_name: "", parent_name_2: "",
  parent_phone: "", parent_email: "", parent_occupation: "", previous_school: "",
  class_name: "Playgroup", admission_date: new Date().toISOString().slice(0, 10),
  sibling_id: null, sibling_details: "", notes: "", photo_path: null,
};

export default function Children() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cls, setCls] = useState("");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [form, setForm] = useState(emptyChild);
  const canEdit = user?.role === "admin" || user?.role === "front_desk";

  const load = async () => {
    const params = {};
    if (cls) params.class_name = cls;
    if (q) params.search = q;
    const { data } = await api.get("/children", { params });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cls, q]);

  const save = async () => {
    try {
      await api.post("/children", form);
      toast.success(`${form.name} enrolled!`);
      setOpen(false);
      setForm(emptyChild);
      load();
    } catch (e) {
      toast.error("Could not save");
    }
  };

  const downloadTemplate = () => {
    const csv = [
      "name,date_of_birth,parent_name,parent_name_2,parent_phone,parent_email,parent_occupation,previous_school,class_name,admission_date,sibling_details,notes",
      "Aarav Sharma,2021-05-14,Rohit Sharma,Kavita Sharma,+919876500001,parent@example.com,Software Engineer,ABC Kids School,Nursery,2026-04-01,,"
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "kidzkulam_children_import_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const importCsv = async () => {
    if (!importFile) {
      toast.error("Please choose a CSV file");
      return;
    }
    const fd = new FormData();
    fd.append("file", importFile);
    setImporting(true);
    setImportResult(null);
    try {
      const { data } = await api.post("/children/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success(`${data.imported} children imported`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    items.forEach((c) => { (g[c.class_name] ||= []).push(c); });
    return g;
  }, [items]);

  const initials = (n) => n.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const ageFrom = (dob) => {
    if (!dob) return "";
    const d = new Date(dob);
    const now = new Date();
    const years = now.getFullYear() - d.getFullYear();
    const months = now.getMonth() - d.getMonth();
    const total = years * 12 + months;
    if (total < 24) return `${total} mo`;
    return `${Math.floor(total / 12)}y ${total % 12}m`;
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black">{user?.role === "teacher" ? "My Class" : "Children Directory"}</h1>
          <p className="text-muted-foreground font-medium">Every little student at Kidzkulam.</p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => { setImportOpen(true); setImportResult(null); }}
              className="rounded-full h-12 px-5 font-bold"
            >
              <Upload size={18} /> Import Students
            </Button>
            <Button data-testid="add-child-btn" onClick={() => setOpen(true)}
              className="rounded-full h-12 px-6 font-bold hover:-translate-y-0.5 hover:shadow-md transition-all">
              <Plus size={18} /> Enroll Child
            </Button>
          </div>
        )}
      </header>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            data-testid="child-search"
            placeholder="Search by name..."
            value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-11 h-12 rounded-full"
          />
        </div>
        {user?.role !== "teacher" && (
          <Select value={cls || "__all"} onValueChange={(v) => setCls(v === "__all" ? "" : v)}>
            <SelectTrigger data-testid="child-class-filter" className="h-12 rounded-full w-48"><SelectValue placeholder="All classes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All classes</SelectItem>
              {CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {Object.keys(grouped).length === 0 && (
        <div className="text-center py-16 text-muted-foreground font-medium">
          No children found.
        </div>
      )}

      {Object.entries(grouped).map(([className, kids]) => (
        <section key={className} className="space-y-3">
          <h2 className="text-lg font-extrabold flex items-center gap-2">
            <span className="chip-green px-3 py-1 rounded-full text-xs uppercase tracking-widest">{className}</span>
            <span className="text-muted-foreground text-sm font-semibold">{kids.length} kids</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {kids.map((c, i) => (
              <Link
                key={c.id}
                to={`/children/${c.id}`}
                data-testid={`child-card-${c.id}`}
                className="card-soft rounded-3xl p-5 flex items-center gap-4 group"
              >
                <div className={`w-14 h-14 rounded-2xl ${PASTELS[i % PASTELS.length]} flex items-center justify-center font-black text-lg`}>
                  {initials(c.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold truncate">{c.name}</div>
                  <div className="text-sm text-muted-foreground truncate">
                    {ageFrom(c.date_of_birth)} · {c.parent_name}
                  </div>
                </div>
                <Baby className="opacity-30 group-hover:opacity-70 transition-opacity" />
              </Link>
            ))}
          </div>
        </section>
      ))}

      {/* Import children dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="rounded-3xl max-w-xl">
          <DialogHeader>
            <DialogTitle>Import students from CSV</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Download the template, fill it in Excel or Google Sheets, then save/export it as CSV and upload here.
            </p>

            <Button variant="outline" onClick={downloadTemplate} className="rounded-full">
              <Download size={18} /> Download CSV Template
            </Button>

            <div>
              <Label>CSV File</Label>
              <Input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="mt-1 rounded-xl"
              />
            </div>

            {importResult && (
              <div className="rounded-2xl border p-4 text-sm space-y-2">
                <div className="font-bold">
                  Imported: {importResult.imported} · Skipped: {importResult.skipped}
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="max-h-44 overflow-y-auto text-muted-foreground space-y-1">
                    {importResult.errors.map((err, idx) => (
                      <div key={idx}>
                        Row {err.row}{err.name ? ` (${err.name})` : ""}: {err.errors.join(", ")}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              onClick={importCsv}
              disabled={!importFile || importing}
              className="rounded-full"
            >
              {importing ? "Importing..." : "Import Students"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add child dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-2xl">
          <DialogHeader><DialogTitle>Enroll a new child</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto pr-1">
            <div><Label>Name</Label><Input data-testid="new-child-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl" /></div>
            <div><Label>Date of Birth</Label><Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="h-11 rounded-xl" /></div>
            <div><Label>Parent 1 Name</Label><Input value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} className="h-11 rounded-xl" /></div>
            <div><Label>Parent 2 Name</Label><Input value={form.parent_name_2} onChange={(e) => setForm({ ...form, parent_name_2: e.target.value })} className="h-11 rounded-xl" /></div>
            <div><Label>Phone</Label><Input value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} className="h-11 rounded-xl" /></div>
            <div><Label>Email</Label><Input value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} className="h-11 rounded-xl" /></div>
            <div><Label>Occupation</Label><Input value={form.parent_occupation} onChange={(e) => setForm({ ...form, parent_occupation: e.target.value })} className="h-11 rounded-xl" /></div>
            <div><Label>Previous School</Label><Input value={form.previous_school} onChange={(e) => setForm({ ...form, previous_school: e.target.value })} className="h-11 rounded-xl" /></div>
            <div>
              <Label>Class</Label>
              <Select value={form.class_name} onValueChange={(v) => setForm({ ...form, class_name: v })}>
                <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Admission Date</Label><Input type="date" value={form.admission_date} onChange={(e) => setForm({ ...form, admission_date: e.target.value })} className="h-11 rounded-xl" /></div>
            <div className="col-span-2"><Label>Sibling details</Label><Input value={form.sibling_details} onChange={(e) => setForm({ ...form, sibling_details: e.target.value })} className="h-11 rounded-xl" /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="save-child" onClick={save} className="rounded-full">Enroll</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
